import RequestAbortSignal from '@/Enums/Pixels/RequestAbortSignal.enum';
import type { IPixelsFilterModel } from '@/Interfaces/Filters/FilterModel.interface';
import { useRequestSaver } from '@/hooks/RequestSaver';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectHideWhitelabels } from '@/store/selectors/me.selectors';
import {
  selectLoadingPage,
  selectLoadingPixels,
  selectPixelsEvents,
  selectPixelsFilters,
  selectPixelsReport,
  selectPixelsSegments,
} from '@/store/selectors/pixel.selectors';
import { selectSharedApiLoading } from '@/store/selectors/sharedApi.selectors';
import { mergeEventData, resetPixelsPageState, setFilters } from '@/store/slices/pixelsSlice';
import { getPixels, getPixelsReport, getSegments } from '@/store/slices/pixelsThunks';
import { getClients, getWhitelabels } from '@/store/slices/sharedApi.slice';
import { Paper } from '@mui/material';
import { useCallback, useEffect } from 'react';
import DataGrid from './DataGrid';

const Pixels = () => {
  const requestSaver = useRequestSaver();
  const dispatch = useAppDispatch();

  const hideWhitelabels = useAppSelector(selectHideWhitelabels);
  const loadingFilterSource = useAppSelector(selectSharedApiLoading);
  const loadingPage = useAppSelector(selectLoadingPage);
  const loadingPixels = useAppSelector(selectLoadingPixels);
  const filters = useAppSelector(selectPixelsFilters);
  const { pixels } = useAppSelector(selectPixelsEvents);
  const { loadingSegments } = useAppSelector(selectPixelsSegments);
  const { loadingPixelsReport } = useAppSelector(selectPixelsReport);

  useEffect(() => {
    return () => {
      requestSaver.requestSaverAbortAll();
      dispatch(resetPixelsPageState());
    };
  }, []);

  //#region data requests
  const loadPixels = useCallback(() => {
    dispatch(getPixels(requestSaver.requestSaverSetCancellation(RequestAbortSignal.Pixels)));
  }, [dispatch]);

  const loadPixelsReport = useCallback(() => {
    dispatch(getPixelsReport(requestSaver.requestSaverSetCancellation(RequestAbortSignal.PixelsReport)));
  }, [dispatch]);

  const loadSegments = useCallback(() => {
    dispatch(getSegments(requestSaver.requestSaverSetCancellation(RequestAbortSignal.PixelsSegments)));
  }, [dispatch]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  useEffect(() => {
    if (hideWhitelabels) {
      dispatch(getClients());
    } else {
      dispatch(getWhitelabels());
    }
  }, [dispatch, hideWhitelabels]);

  useEffect(() => {
    if (!loadingSegments && !loadingFilterSource) {
      loadPixels();
      loadPixelsReport();
    }
  }, [
    loadingFilterSource,
    loadingSegments,
    filters.whitelabels,
    filters.clients,
    filters.advertisers,
    filters.active,
    loadPixels,
    loadPixelsReport,
  ]);

  useEffect(() => {
    if (!loadingPixels && !loadingPixelsReport) {
      dispatch(mergeEventData());
    }
  }, [dispatch, loadingPixels, loadingPixelsReport]);
  //#endregion

  //#region dispatches
  const _setFilters = useCallback(
    (newFilters: Partial<IPixelsFilterModel>) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );
  //#endregion

  return (
    <Paper className='grid grid-cols-1' sx={{ margin: '30px' }}>
      <DataGrid
        data={pixels}
        loading={loadingPage}
        filters={filters}
        loadingMetrics={loadingPixelsReport}
        setFilters={_setFilters}
      />
    </Paper>
  );
};

export default Pixels;
